import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { OLYMP_API_BASE_URL } from '../../config/api';

const LLM_MODELS_URL = `${OLYMP_API_BASE_URL}/olymp/settings/llm-models/`;

const emptyDraft = { provider: '', model: '', label: '', apiKey: '' };

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const KNOWN_MODEL_FIELDS = ['id', 'provider', 'model', 'label', 'active', 'has_credential'];

// Rueckmeldung Runde 1, NEU B3: eine Serverantwort wird nie roh in den
// Zustand uebernommen. Ein Feld wie ein versehentlich mitgeschickter
// Schluessel (Backend-Fehler oder ein zukuenftiges Feld) faellt so heraus,
// statt fuer die Lebensdauer der Seite im Zustand zu verbleiben (AK3).
export const sanitizeModelEntry = (raw) =>
  KNOWN_MODEL_FIELDS.reduce((acc, field) => {
    acc[field] = raw[field];
    return acc;
  }, {});

// #94: der Schluessel ist ausschliesslich schreibend -- kein Ruecklesepfad,
// keine maskierte Anzeige. `has_credential` wird deshalb als Status-Chip
// dargestellt, nie als Platzhalter, der wie ein Schluessel aussieht.
const LlmModelSettings = () => {
  const { t } = useTranslation();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [testResults, setTestResults] = useState({});

  // Laedt die Liste frisch vom Server. Liefert zurueck, ob es geklappt hat,
  // damit `handleAddModel` weiss, ob die Liste inzwischen den aktuellen
  // Stand hat oder ob ein Fallback noetig ist.
  const loadModels = async () => {
    setLoading(true);
    try {
      const response = await axios.get(LLM_MODELS_URL, getAuthHeaders());
      const data = response.data;
      const entries = Array.isArray(data) ? data : data?.results || [];
      setModels(entries.map(sanitizeModelEntry));
      setLoadFailed(false);
      return true;
    } catch (err) {
      setError(t('settings.sections.api.llmModels.loadError'));
      setLoadFailed(true);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Laedt einmalig beim Einhaengen. `t` bewusst nicht in den Dependencies --
  // ein Effekt, der bei jedem Wechsel der Uebersetzungsfunktion neu laedt,
  // holt bei jedem Render erneut die Liste (und faengt sich damit mitten im
  // Absende- oder Loeschvorgang selbst wieder in den Ladezustand zurueck).
  useEffect(() => {
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDraftChange = (field) => (event) => {
    const { value } = event.target;
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddModel = async (event) => {
    event.preventDefault();
    const { provider, model, label, apiKey } = draft;
    if (!provider.trim() || !model.trim() || !apiKey.trim()) return;

    // Der Schluessel verlaesst den Zustand hier, noch bevor die Anfrage
    // beantwortet ist -- er steht nie laenger im Anwendungszustand als fuer
    // den Absendevorgang (AK3).
    setDraft((prev) => ({ ...prev, apiKey: '' }));
    setSaving(true);
    setError('');
    try {
      const response = await axios.post(
        LLM_MODELS_URL,
        { provider: provider.trim(), model: model.trim(), label: label.trim(), api_key: apiKey },
        getAuthHeaders(),
      );
      // Rueckmeldung Runde 1, NEU B3: die POST-Antwort wird nie direkt in
      // den Zustand uebernommen -- sonst koennte ein Feld, das der Server
      // versehentlich mitschickt (etwa der Schluessel selbst), beliebig
      // lange im Zustand liegen bleiben. Stattdessen wird die Liste neu vom
      // Server geladen, derselbe Pfad, der auch beim Einhaengen nur bekannte
      // Felder uebernimmt.
      const reloaded = await loadModels();
      if (!reloaded) {
        // Neuladen fehlgeschlagen: das gerade angelegte Modell bleibt
        // trotzdem sichtbar (Rueckmeldung Runde 2, N1) -- ein vorangegangener
        // oder gerade aufgetretener Ladefehler macht einen erfolgreichen
        // Anlegevorgang nicht ungeschehen. Auch hier nur die bekannten
        // Felder aus der POST-Antwort, nie den Schluessel.
        setModels((prev) => [...prev, sanitizeModelEntry(response.data)]);
        setLoadFailed(false);
      }
      setDraft(emptyDraft);
    } catch (err) {
      setError(t('settings.sections.api.llmModels.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    try {
      await axios.delete(`${LLM_MODELS_URL}${id}/`, getAuthHeaders());
      setModels((prev) => prev.filter((entry) => entry.id !== id));
    } catch (err) {
      setError(t('settings.sections.api.llmModels.deleteError'));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleTestConnection = async (id) => {
    setTestResults((prev) => ({ ...prev, [id]: { status: 'running' } }));
    try {
      const response = await axios.post(`${LLM_MODELS_URL}${id}/test/`, {}, getAuthHeaders());
      setTestResults((prev) => ({
        ...prev,
        [id]: {
          status: response.data?.success ? 'success' : 'error',
          latencyMs: response.data?.latency_ms,
          message: response.data?.message || '',
        },
      }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [id]: { status: 'error', message: err.response?.data?.detail || '' },
      }));
    }
  };

  const renderTestResult = (id) => {
    const result = testResults[id];
    if (!result) return null;
    if (result.status === 'running') {
      return <Typography variant="caption">{t('settings.sections.api.llmModels.testing')}</Typography>;
    }
    if (result.status === 'success') {
      return (
        <Typography variant="caption" color="success.main">
          {t('settings.sections.api.llmModels.testSuccess', { latency: result.latencyMs })}
        </Typography>
      );
    }
    return (
      <Typography variant="caption" color="error.main">
        {t('settings.sections.api.llmModels.testError', {
          message: result.message ? `: ${result.message}` : '',
        })}
      </Typography>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={24} />
      ) : loadFailed ? null : models.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('settings.sections.api.llmModels.empty')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('settings.sections.api.llmModels.columns.provider')}</TableCell>
              <TableCell>{t('settings.sections.api.llmModels.columns.model')}</TableCell>
              <TableCell>{t('settings.sections.api.llmModels.columns.label')}</TableCell>
              <TableCell>{t('settings.sections.api.llmModels.columns.status')}</TableCell>
              <TableCell>{t('settings.sections.api.llmModels.columns.credential')}</TableCell>
              <TableCell>{t('settings.sections.api.llmModels.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {models.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{entry.provider}</TableCell>
                <TableCell>{entry.model}</TableCell>
                <TableCell>{entry.label}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={entry.active ? 'success' : 'default'}
                    label={t(
                      entry.active
                        ? 'settings.sections.api.llmModels.status.active'
                        : 'settings.sections.api.llmModels.status.inactive',
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t(
                      entry.has_credential
                        ? 'settings.sections.api.llmModels.credential.present'
                        : 'settings.sections.api.llmModels.credential.missing',
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => handleTestConnection(entry.id)}
                      disabled={testResults[entry.id]?.status === 'running'}
                    >
                      {t('settings.sections.api.llmModels.testConnection')}
                    </Button>
                    {renderTestResult(entry.id)}
                    <IconButton
                      size="small"
                      aria-label={`${t('settings.sections.api.llmModels.deleteAction')}: ${entry.label}`}
                      onClick={() => setDeleteTarget(entry)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Box
        component="form"
        onSubmit={handleAddModel}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}
      >
        <Typography variant="subtitle2">{t('settings.sections.api.llmModels.addTitle')}</Typography>
        <TextField
          label={t('settings.sections.api.llmModels.fields.provider')}
          value={draft.provider}
          onChange={handleDraftChange('provider')}
          size="small"
        />
        <TextField
          label={t('settings.sections.api.llmModels.fields.model')}
          value={draft.model}
          onChange={handleDraftChange('model')}
          size="small"
        />
        <TextField
          label={t('settings.sections.api.llmModels.fields.label')}
          value={draft.label}
          onChange={handleDraftChange('label')}
          size="small"
        />
        <TextField
          label={t('settings.sections.api.llmModels.fields.apiKey')}
          value={draft.apiKey}
          onChange={handleDraftChange('apiKey')}
          type="password"
          autoComplete="off"
          size="small"
        />
        <Button type="submit" variant="contained" disabled={saving}>
          {t('settings.buttons.add')}
        </Button>
      </Box>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('settings.sections.api.llmModels.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('settings.sections.api.llmModels.deleteConfirm', { label: deleteTarget?.label })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('settings.buttons.cancel')}</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            {t('settings.sections.api.llmModels.deleteButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LlmModelSettings;
